// Week 5 — Realtime listener for shared/assign__* docs where the current
// user is a participant. Detects new contractor submissions (expenses /
// estimates) since first connect and surfaces them as a banner in Shell.

import { useEffect, useState } from 'react'
import { onSnapshot, query, where } from 'firebase/firestore'
import { sharedCol } from '../lib/firebase/firestore'
import { useAuth } from './useAuth'
import type { Assignment } from '../types'

export interface AssignmentAlert {
  assignId: string
  projectName: string
  kind: 'expense' | 'estimate'
  count: number      // how many new items since last read
  ts: number
}

function seenKey(uid: string) { return `jsp:assignments:seen:${uid}` }

function loadSeen(uid: string): Record<string, { expCount: number; estCount: number }> {
  try {
    const raw = localStorage.getItem(seenKey(uid))
    return raw ? JSON.parse(raw) : {}
  } catch { return {} }
}
function saveSeen(uid: string, seen: Record<string, { expCount: number; estCount: number }>) {
  try { localStorage.setItem(seenKey(uid), JSON.stringify(seen)) } catch {}
}

/**
 * Subscribe to assignments where current uid participates. Returns the live
 * list + an alerts list (new submissions since last ack). `ackAlert` marks
 * an assignment's current counts as seen so the banner clears.
 */
export function useAssignmentsRealtime(): {
  assignments: Assignment[]
  alerts: AssignmentAlert[]
  ackAlert: (assignId: string) => void
} {
  const { uid, isReady } = useAuth()
  const [assignments, setAssignments] = useState<Assignment[]>([])
  const [seen, setSeen] = useState<Record<string, { expCount: number; estCount: number }>>({})

  useEffect(() => {
    if (!uid || !isReady) return
    setSeen(loadSeen(uid))

    // Shared docs where uid participates — filter to assign_* client-side.
    const q = query(
      sharedCol(),
      where('participantUids', 'array-contains', uid),
    )

    const unsub = onSnapshot(q, (snap) => {
      const list: Assignment[] = []
      snap.forEach((docSnap) => {
        if (!docSnap.id.startsWith('assign_')) return
        const d = docSnap.data() as Assignment
        list.push({ ...d, id: docSnap.id })
      })
      setAssignments(list)
    }, (err) => {
      // Listener errors are non-fatal — permissions or temporary network.
      // Log via Sentry is already wired at app level; stay silent here.
      console.warn('[assignments] listener error', err)
    })
    return unsub
  }, [uid, isReady])

  const alerts: AssignmentAlert[] = []
  for (const a of assignments) {
    // Only alert the investor (owner). Contractors see the assignment itself,
    // not a "new submission" banner meant for the owner.
    if (a.investorUid !== uid) continue
    const expCount = a.contractorExpenses?.length  ?? 0
    const estCount = a.contractorEstimates?.length ?? 0
    const s = seen[a.id] ?? { expCount: 0, estCount: 0 }
    const newExp = Math.max(0, expCount - s.expCount)
    const newEst = Math.max(0, estCount - s.estCount)
    if (newExp > 0) {
      alerts.push({ assignId: a.id, projectName: a.projectName ?? `Project #${a.projId}`, kind: 'expense', count: newExp, ts: a.assignedAt })
    }
    if (newEst > 0) {
      alerts.push({ assignId: a.id, projectName: a.projectName ?? `Project #${a.projId}`, kind: 'estimate', count: newEst, ts: a.assignedAt })
    }
  }

  function ackAlert(assignId: string) {
    if (!uid) return
    const assignment = assignments.find((a) => a.id === assignId)
    if (!assignment) return
    const next = {
      ...seen,
      [assignId]: {
        expCount: assignment.contractorExpenses?.length  ?? 0,
        estCount: assignment.contractorEstimates?.length ?? 0,
      },
    }
    setSeen(next)
    saveSeen(uid, next)
  }

  return { assignments, alerts, ackAlert }
}
